import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { PageHeader } from "../components/PageHeader";
import { cmsHost, displayContainerId, displayEnvironment } from "../lib/languages";
import type { HealthResponse } from "../lib/types";

export function SettingsPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/health");
        if (!res.ok) return;
        const data = (await res.json()) as HealthResponse;
        if (!cancelled) setHealth(data);
      } catch {
        // Keep Settings calm when Functions are offline.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const items = [
    { label: "CMS host", value: cmsHost() },
    { label: "CloudKit container", value: health?.containerId ?? displayContainerId() },
    { label: "CloudKit environment", value: health?.environment ?? displayEnvironment() },
    {
      label: "Apple auth",
      value: health ? (health.appleAuthConfigured ? "Configured" : "Missing secrets") : "Unknown",
    },
    {
      label: "CloudKit server key",
      value: health
        ? health.cloudKitConfigured
          ? "Configured"
          : "Not set"
        : "Unknown",
    },
    {
      label: "Firestore",
      value: health
        ? health.firestoreConfigured
          ? `Configured${health.firebaseProjectId ? ` · ${health.firebaseProjectId}` : ""}`
          : "Not set (C5)"
        : "Unknown",
    },
  ];

  return (
    <AppShell>
      <PageHeader
        eyebrow="Account"
        title="Settings"
        description={
          <>
            Environment labels and setup notes. Private keys stay in Pages secrets /{" "}
            <code>.dev.vars</code> — never <code>VITE_</code>.
          </>
        }
      />

      <div className="panel panel-pad stack">
        <div className="section-title">
          <h2>Environment</h2>
          <p>Values reported by the Worker when available.</p>
        </div>
        <div className="table-wrap">
          <table className="data settings-rows">
            <tbody>
              {items.map((row) => (
                <tr key={row.label}>
                  <th style={{ width: "36%" }}>{row.label}</th>
                  <td>
                    <code>{row.value}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel panel-pad stack">
        <div className="section-title">
          <h2>Setup</h2>
          <p>Infrequently changed configuration for this admin surface.</p>
        </div>
        <ol className="muted" style={{ margin: 0, paddingLeft: "1.15rem", lineHeight: 1.7 }}>
          <li>
            Confirm the CloudKit container in Xcode / Apple Developer (
            <Link className="link-inline" to="/schema">
              Schema
            </Link>
            ).
          </li>
          <li>
            Register Services ID domains for <code>{cmsHost()}</code> with return URL{" "}
            <code>https://{cmsHost()}/auth/callback</code>.
          </li>
          <li>
            Set Pages secrets: <code>APPLE_CLIENT_ID</code>, <code>SESSION_SECRET</code>,{" "}
            <code>APPLE_ADMIN_SUBS</code> / <code>APPLE_ADMIN_EMAILS</code>.
          </li>
          <li>
            Server-to-server: <code>CLOUDKIT_KEY_ID</code> and <code>CLOUDKIT_PRIVATE_KEY</code>.
          </li>
          <li>
            Firestore mirror (C5): <code>FIREBASE_PROJECT_ID</code>,{" "}
            <code>FIRESTORE_CLIENT_EMAIL</code>, <code>FIRESTORE_PRIVATE_KEY</code>.
          </li>
          <li>
            Attach custom domain <strong>{cmsHost()}</strong> on the Cloudflare Pages project.
          </li>
        </ol>
      </div>
    </AppShell>
  );
}
